import { recordZ4RumError } from '@marageti/z4-lib';

function stringifyRejectionReason(reason: unknown): string {
  if (reason === undefined) return 'undefined';
  if (reason === null) return 'null';
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  if (typeof reason === 'object') {
    try {
      return JSON.stringify(reason);
    } catch {
      return '[object Object]';
    }
  }
  return String(reason);
}

/**
 * Wraps a floating promise so it cannot surface as an unhandled rejection.
 * Logs to the console in dev and reports to RUM with a labeled context in prod.
 *
 *
 * Usage:
 *   debugPromise('myComponent:onSubmit.fetch', fetch(...));
 */
export default function debugPromise(
  context: string,
  work: Promise<unknown> | void | undefined,
): void {
  const callerStackAtFire = new Error(`[debugPromise] ${context}`).stack;
  // eslint-disable-next-line no-void
  void Promise.resolve(work).catch((reason: unknown) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[TravelApp debugPromise rejection]', { context, reason, callerStackAtFire });
      return;
    }

    const summary = stringifyRejectionReason(reason);
    const metaLines = [
      '[TravelApp debugPromise]',
      `context=${context}`,
      `summary=${summary}`,
      callerStackAtFire ? `callerStackAtFire:\n${callerStackAtFire}` : '',
    ].filter(Boolean).join('\n');

    if (reason instanceof Error) {
      recordZ4RumError(reason, { componentStack: metaLines });
      return;
    }

    recordZ4RumError(
      new Error(`[TravelApp debugPromise] ${context}: ${summary}`),
      { componentStack: metaLines },
    );
  });
}
